import React, { useEffect, useState } from 'react';
import api, { getApiErrorMessage } from '../../api';
import { formatSize } from './shareUtils';
import './Presentations.css';

const STATUS_NOTE = { checking: ' · проверяется', review: ' · на проверке у модератора' };

/**
 * Настройка «Поделиться презентацией» в опросе (kind=poll) или сессии вопросов (kind=qa).
 * Сохраняется сразу. value — { enabled, presentation_id, qr }.
 */
function ShareSettings({ kind, id, value, paid, onChange }) {
    const [files, setFiles] = useState(null);
    const [requests, setRequests] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    // Меняем сразу, не дожидаясь сервера; при ошибке возвращаем как было
    const [current, setCurrent] = useState(() => ({ enabled: false, presentation_id: null, qr: true, ...(value || {}) }));

    useEffect(() => {
        if (!paid) return;
        api.get('/presentations').then((res) => setFiles(res.data.items)).catch(() => setFiles([]));
        const param = kind === 'poll' ? `poll_id=${id}` : `qa_session_id=${id}`;
        api.get(`/presentations/requests?${param}`).then((res) => setRequests(res.data.items)).catch(() => setRequests(null));
    }, [kind, id, paid]);

    const save = async (patch) => {
        const previous = current;
        const next = { ...current, ...patch };
        setCurrent(next);
        setSaving(true);
        setError('');
        try {
            const url = kind === 'poll' ? `/polls/${id}/share` : `/qa-sessions/${id}/share`;
            const res = await api.put(url, { enabled: next.enabled, presentation_id: next.presentation_id, qr: next.qr });
            setCurrent(res.data);
            onChange?.(res.data);
        } catch (err) {
            setCurrent(previous);
            setError(getApiErrorMessage(err, 'Не удалось сохранить'));
        } finally {
            setSaving(false);
        }
    };

    const selected = files?.find((f) => f.id === current.presentation_id);
    const missing = current.presentation_id && files && !selected; // файл удалён или истёк

    return (
        <div className="share-settings">
            <label className="share-settings__switch">
                <input
                    type="checkbox" checked={current.enabled} disabled={!paid}
                    onChange={(e) => save({ enabled: e.target.checked })}
                />
                <span>
                    <b>Поделиться презентацией {!paid && <span className="share-pro">PRO</span>}</b>
                    <small>
                        {paid
                            ? 'Участники оставляют почту после ответа и получают ссылку на файл и промокод на СВОД.'
                            : 'Доступно на тарифах «Расширенный» и «Корпоративный».'}
                    </small>
                </span>
            </label>
            {paid && current.enabled && (
                <div className="share-settings__body">
                    <label className="share-settings__field">
                        <span>Файл</span>
                        <select
                            value={missing ? '' : (current.presentation_id || '')} disabled={saving || files === null}
                            onChange={(e) => save({ presentation_id: e.target.value ? Number(e.target.value) : null })}
                        >
                            <option value="">Загружу позже</option>
                            {(files || []).map((f) => (
                                <option key={f.id} value={f.id}>
                                    {f.title} · {formatSize(f.size)} · ещё {f.days_left} дн.{STATUS_NOTE[f.status] || ''}
                                </option>
                            ))}
                        </select>
                    </label>
                    {!current.presentation_id && (
                        <p className="share-settings__hint">Почты участников сохранятся, письма уйдут сами, когда вы выберете файл.</p>
                    )}
                    {missing && <p className="share-settings__warn">Выбранный файл удалён или срок его хранения закончился — выберите другой.</p>}
                    <a className="share-settings__link" href="/my-presentations" target="_blank" rel="noreferrer">Загрузить или удалить файлы →</a>
                    <label className="share-settings__check">
                        <input type="checkbox" checked={current.qr} onChange={(e) => save({ qr: e.target.checked })} />
                        <span>QR-код «Получить презентацию» на экране {kind === 'poll' ? 'результатов' : 'для проектора'}</span>
                    </label>
                    {requests && requests.length > 0 && (
                        <p className="share-settings__stat">
                            Запросили: <b>{requests.length}</b>
                            {' · '}отправлено {requests.filter((r) => r.status === 'sent').length}
                            {' · '}ждут файл {requests.filter((r) => r.status === 'pending').length}
                        </p>
                    )}
                </div>
            )}
            {error && <p className="share-settings__error" role="alert">{error}</p>}
        </div>
    );
}

export default ShareSettings;
